import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, CheckCircle, AlertCircle, Clock, Database, Settings } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function IntegrationCard({ integration, onSync, onConfigure }) {
  const platformInfo = {
    google_ads: { name: 'Google Ads', icon: '🔍', color: '#4285F4' },
    meta_ads: { name: 'Meta Ads', icon: '📘', color: '#1877F2' },
    salesforce: { name: 'Salesforce', icon: '☁️', color: '#00A1E0' },
    hubspot: { name: 'HubSpot', icon: '🧡', color: '#FF7A59' }
  };

  const info = platformInfo[integration.platform] || { name: integration.platform, icon: '🔌', color: '#6B7280' };

  return (
    <div 
      className="card" 
      style={{ 
        border: integration.enabled ? '2px solid var(--success-500)' : '1px solid var(--border-light)',
        position: 'relative',
        overflow: 'visible'
      }}
      data-testid={`integration-${integration.platform}`}
    >
      {/* Status indicator */}
      {integration.enabled && (
        <div style={{
          position: 'absolute',
          top: '-8px',
          right: '16px',
          background: 'var(--success-500)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <CheckCircle size={14} />
          Active
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
        <div style={{
          width: '64px',
          height: '64px',
          background: info.color,
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem'
        }}>
          {info.icon}
        </div>

        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '4px' }}>
            {info.name}
          </h3>
          <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: '12px' }}>
            {integration.platform === 'google_ads' && 'Search & Display advertising platform'}
            {integration.platform === 'meta_ads' && 'Facebook & Instagram advertising'}
            {integration.platform === 'salesforce' && 'CRM and revenue attribution'}
            {integration.platform === 'hubspot' && 'Marketing automation & CRM'}
          </p>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
            {integration.credentials?.configured && (
              <span className="badge badge-success">
                Authenticated
              </span>
            )}
            {integration.last_sync && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={12} />
                Last sync: {new Date(integration.last_sync).toLocaleString()}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => onSync(integration._id)}
              className="btn btn-primary"
              style={{ fontSize: '0.875rem', padding: '8px 16px' }}
              data-testid={`sync-${integration.platform}`}
            >
              <RefreshCw size={16} />
              Sync Now
            </button>
            <button
              onClick={() => onConfigure(integration)}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem', padding: '8px 16px' }}
            >
              <Settings size={16} />
              Configure
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddIntegrationModal({ onClose, onAdd }) {
  const [platform, setPlatform] = useState('google_ads');
  const [credentials, setCredentials] = useState({});
  const [saving, setSaving] = useState(false);

  const platforms = [
    { id: 'google_ads', name: 'Google Ads', fields: ['client_id', 'client_secret', 'developer_token', 'customer_id'] },
    { id: 'meta_ads', name: 'Meta Ads', fields: ['access_token', 'ad_account_id'] },
    { id: 'salesforce', name: 'Salesforce', fields: ['instance_url', 'access_token', 'client_id'] },
    { id: 'hubspot', name: 'HubSpot', fields: ['access_token', 'portal_id'] }
  ];

  const selectedPlatform = platforms.find(p => p.id === platform);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await axios.post(`${API_BASE_URL}/api/integrations/configure`, {
        platform,
        credentials,
        enabled: true
      });
      onAdd();
      onClose();
    } catch (error) {
      console.error('Error configuring integration:', error);
      alert('Error configuring integration. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          width: '600px',
          padding: '24px'
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="add-integration-modal"
      >
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '24px' }}>
          Add Integration
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px' }}>
              Platform
            </label>
            <select
              value={platform}
              onChange={(e) => {
                setPlatform(e.target.value);
                setCredentials({});
              }}
              style={{
                width: '100%',
                padding: '12px',
                border: '1px solid var(--border-light)',
                borderRadius: '8px',
                fontSize: '0.875rem',
                background: 'white'
              }}
            >
              {platforms.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div style={{ marginBottom: '16px', padding: '12px', background: 'hsl(199, 89%, 96%)', borderRadius: '8px' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--info-700)' }}>
              ℹ️ {selectedPlatform?.name} requires {selectedPlatform?.fields.length} credentials for OAuth authentication
            </p>
          </div>

          {selectedPlatform?.fields.map(field => (
            <div key={field} style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '600', marginBottom: '8px', textTransform: 'capitalize' }}>
                {field.replace('_', ' ')}
              </label>
              <input
                type={field.includes('secret') || field.includes('token') ? 'password' : 'text'}
                value={credentials[field] || ''}
                onChange={(e) => setCredentials({ ...credentials, [field]: e.target.value })}
                placeholder={`Enter ${field}`}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontFamily: field.includes('secret') || field.includes('token') ? 'monospace' : 'inherit'
                }}
                data-testid={`cred-${field}`}
              />
            </div>
          ))}

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ flex: 1, justifyContent: 'center' }}
              data-testid="add-integration-btn"
            >
              {saving ? 'Configuring...' : 'Add Integration'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Integrations() {
  const [integrations, setIntegrations] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [syncing, setSyncing] = useState(null);

  useEffect(() => {
    loadIntegrations();
    loadSyncHistory();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/integrations`);
      setIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error('Error loading integrations:', error);
      // Set demo integrations
      setIntegrations([
        { _id: '1', platform: 'google_ads', enabled: true, credentials: { configured: true }, last_sync: new Date().toISOString() },
        { _id: '2', platform: 'meta_ads', enabled: true, credentials: { configured: true }, last_sync: new Date().toISOString() },
        { _id: '3', platform: 'salesforce', enabled: false, credentials: { configured: false } },
        { _id: '4', platform: 'hubspot', enabled: false, credentials: { configured: false } }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadSyncHistory = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations/sync-history`);
      setSyncHistory(response.data.syncs || []);
    } catch (error) {
      console.error('Error loading sync history:', error);
    }
  };

  const handleSync = async (integrationId) => {
    try {
      setSyncing(integrationId);
      await axios.post(`${API_BASE_URL}/api/integrations/sync`, {
        integration_id: integrationId,
        incremental: true
      });
      alert('Sync completed successfully!');
      await loadIntegrations();
      await loadSyncHistory();
    } catch (error) {
      console.error('Error syncing:', error);
      alert('Sync failed. Check console for details.');
    } finally {
      setSyncing(null);
    }
  };

  const handleAddIntegration = () => {
    setShowAddModal(true);
  };

  const handleIntegrationAdded = () => {
    loadIntegrations();
    loadSyncHistory();
  };

  return (
    <div className="page-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Integrations</h1>
          <p className="page-subtitle">Connect marketing platforms and data sources</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={handleAddIntegration}
          data-testid="add-integration-btn"
        >
          <Plus size={20} />
          Add Integration
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3" style={{ gap: '16px', marginBottom: '24px' }}>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Connected Platforms</p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-600)' }}>
            {integrations.filter(i => i.enabled).length}
          </p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Syncs (7 days)</p>
          <p style={{ fontSize: '2rem', fontWeight: '700' }}>
            {syncHistory.length}
          </p>
        </div>
        <div className="card">
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>Avg Sync Time</p>
          <p style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--accent-600)' }}>
            {syncHistory.length > 0 
              ? `${(syncHistory.reduce((sum, s) => sum + (s.execution_time_ms || 0), 0) / syncHistory.length / 1000).toFixed(1)}s`
              : '0s'
            }
          </p>
        </div>
      </div>

      {/* Integration Cards */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '16px' }}>
            Available Integrations
          </h3>
          <div className="grid grid-cols-2" style={{ gap: '24px', marginBottom: '32px' }}>
            {integrations.map((integration, idx) => (
              <IntegrationCard
                key={idx}
                integration={integration}
                onSync={handleSync}
                onConfigure={() => {}}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sync History */}
      <div className="card">
        <h3 className="card-title">Recent Sync Activity</h3>
        
        {syncHistory.length === 0 ? (
          <div className="empty-state" style={{ marginTop: '24px' }}>
            <Database size={48} className="empty-state-icon" />
            <p>No sync history yet</p>
          </div>
        ) : (
          <div style={{ marginTop: '16px', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-light)', textAlign: 'left' }}>
                  <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Platform</th>
                  <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Entities Synced</th>
                  <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Execution Time</th>
                  <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Quality Score</th>
                  <th style={{ padding: '12px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.slice(0, 10).map((sync, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '12px', textTransform: 'capitalize' }}>
                      {sync.platform?.replace('_', ' ')}
                    </td>
                    <td style={{ padding: '12px', fontWeight: '600' }}>
                      {sync.entities_synced || 0}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {((sync.execution_time_ms || 0) / 1000).toFixed(2)}s
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span className="badge badge-success">
                        {((sync.quality_summary?.avg_completeness || 0.85) * 100).toFixed(0)}%
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '0.875rem' }}>
                      {new Date(sync.synced_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Integration Modal */}
      {showAddModal && (
        <AddIntegrationModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleIntegrationAdded}
        />
      )}
    </div>
  );
}

export default Integrations;
